<script>
  import { onMount, setContext } from 'svelte';
  import { writable } from 'svelte/store';
  import Viewport from '@components/Viewport.svelte';
  import Header from '@components/Header.svelte';

  import Track from '@utils/track';
  import Url from '@utils/url';
  import NavigationHelper from '@utils/navigation-helper';

  import { browser } from '$app/environment';

  import ConfigAjax from '@ajax/config';
  import GroupAjax from '@ajax/group';
  import GroupBaseScoreAjax from '@ajax/group_base_score';
  import UserAjax from '@ajax/user';
  import RecRequestGroupAjax from '@ajax/rec_request_group';
  import UserAccountFlagAjax from '@ajax/user_account_flag';
  import BookmarkAjax from '@ajax/bookmark';

  import jQuery from 'jquery';

  import ProfileDesktop from '@pages/profile/Desktop.svelte';
  import ProfileMobile from '@pages/profile/Mobile.svelte';
  import VisitorBanner from '@app-components/VisitorBanner.svelte';
  import TermsAndPrivacyFooter from '@app-components/TermsAndPrivacyFooter.svelte';
  import LinktreeBanner from '@app-components/profile/LinktreeBanner.svelte';

  import { initializeProfileStores, getProfileStore } from '@stores/profile';
  import { getGlobalStore } from '@stores/global';
  import { getCurrentUserStore } from '@stores/current_user';
  import BrowserUtils from '@utils/browser-utils';
  import { page } from '$app/stores';
  import CookieHelper from '@utils/cookie-helper';
  import ComponentEvents from '@utils/component-events';
  import { saveUtmsToCookie } from '@utils/utm';

  export let id;
  export let user;
  export let groups;
  export let groupCollections;
  export let groupCollectionsSortOrder;

  initializeProfileStores({
    groupsStore: groups,
    groupCollectionStore: groupCollections,
    groupCollectionSortOrderStore: groupCollectionsSortOrder,
  });

  let configStore = getProfileStore('configStore');
  let groupScoresStore = getProfileStore('groupScoresStore');
  let groupSortOrder = getProfileStore('groupSortOrder');
  let followCountStore = getProfileStore('followCountStore');
  let linktreePromptStore = getProfileStore('linktreePromptStore');
  let recRequestGroupStore = getGlobalStore('recRequestGroupStore');
  let bookmarkStore = getGlobalStore('bookmarkStore');

  let groupsStore = getProfileStore('groupsStore');

  let loadingGroups = true;
  const allProfileGroupsLoadedStore = writable(false);
  setContext('allProfileGroupsLoaded', allProfileGroupsLoadedStore);

  let currentUser = getCurrentUserStore('default');

  $: pageName = user && currentUser && user.id === currentUser.id ? 'my-profile' : 'profile';
  let changeUrlIfUsernameIsStale = function () {
    if (user && user.id) {
      if (location.pathname != user.profile_path) {
        Url.changeUrl(`${user.profile_path}${location.search}`);
      }
    }
  };

  let markProfileAsSeen = function () {
    currentUser.set({ ...currentUser, viewed_profile: true });
  };

  let loadConfig = async function () {
    configStore.set(await ConfigAjax.user());
  };

  let loadUser = async function () {
    try {
      loadRecRequestGroup();
      loadFollowCounts();
    } catch (err) {
      console.error(err);
    }

    changeUrlIfUsernameIsStale();
    trackProfileView();

    if (user && currentUser && user.id === currentUser.id) {
      markProfileAsSeen();
      loadLinktreeShow();
    }
  };

  let loadGroups = async function () {
    try {
      let response = await GroupAjax.fetch(id, {
        data: {
          exclude_ids: groups.map((x) => x.id),
        },
      });
      groupsStore.addCollection(response.groups);
      groupSortOrder.set(response.groups_sort_order);

      loadingGroups = false;
      allProfileGroupsLoadedStore.set(true);

      if (currentUser && currentUser.user_flag && !currentUser.user_content_hide) {
        let groupScores = await GroupBaseScoreAjax.bulkInfo(groupsStore.map((group) => group.id));
        groupScoresStore.set(groupScores);
      }
    } catch (err) {
      console.error(err);
    }
  };

  let loadRecRequestGroup = async function () {
    if (currentUser && currentUser.id) {
      let recRequestGroup = await RecRequestGroupAjax.fetch();
      recRequestGroupStore.set(recRequestGroup);
    }
  };

  let loadFollowCounts = async function () {
    let followCounts = await UserAjax.follow_counts(user);
    followCountStore.set({
      followerCount: followCounts.follower || 0,
      followingCount: followCounts.following || 0,
    });
  };

  let loadLinktreeShow = async function () {
    let response = await UserAccountFlagAjax.showLinktreeProfile();
    linktreePromptStore.set({
      banner: response.banner,
      social: response.social,
    });
  };

  let loadBookmarks = async function () {
    if (currentUser && currentUser.id && bookmarkStore && bookmarkStore.isNew) {
      bookmarkStore.set(await BookmarkAjax.fetch());
    }
  };

  let setScrollPosition = function () {
    try {
      if (
        window.profileScrollPosition == undefined ||
        window.profileHistoryIndex == undefined ||
        history.state['sveltekit:index'] != window.profileHistoryIndex
      ) {
        window.profileScrollPosition = undefined;
        window.profileHistoryIndex = undefined;
        return;
      }

      let times = 0;
      let scrollPosition = window.profileScrollPosition;

      let int = setInterval(() => {
        if (loadingGroups === false || times >= 100) {
          clearInterval(int);
        }

        if (loadingGroups === false) {
          setTimeout(() => jQuery(window).scrollTop(scrollPosition), 0);
          setTimeout(() => jQuery(window).scrollTop(scrollPosition), 100);
          setTimeout(() => jQuery(window).scrollTop(scrollPosition), 200);
          setTimeout(() => jQuery(window).scrollTop(scrollPosition), 300);

          window.profileScrollPosition = undefined;
          window.profileHistoryIndex = undefined;
        }

        times += 1;
      }, 100);
    } catch {}
  };

  let onScroll = function () {
    try {
      let top = jQuery(window).scrollTop();

      if (top && top > 0) {
        window.profileScrollPosition = top;
        window.profileHistoryIndex = history.state['sveltekit:index'];
      }
    } catch {}
  };

  let trackProfileView = function () {
    if (user && user.id) {
      let fromPath = NavigationHelper.getFromPath();

      Track.profileView({ currentUser, user, fromPath });
      CookieHelper.setProfileVisitsCookie(user);
    }
  };

  let documentClickTriggers = function () {
    ComponentEvents.trigger('close-all-options-menu');
  };

  if (browser) {
    saveUtmsToCookie();
  }

  onMount(() => {
    setScrollPosition();
    Promise.all([loadGroups(), loadConfig(), loadUser(), loadBookmarks()])
      .then(() => {
        const requestedCollectionId = $page.url.searchParams.get('collection-id');
        if (requestedCollectionId) {
          BrowserUtils.goToElementWithScroll(jQuery(`[data-group-collection-id="${requestedCollectionId}"]`)[0], {
            offsetTop: -100,
            smooth: true,
          });
        }
      })
      .catch((error) => {
        console.error('Error loading data:', error);
      });

    jQuery(window).on('scroll', onScroll);
    let unsub = currentUser.subscribe((x) => (currentUser = x));
    jQuery(document).on('click.triggers', documentClickTriggers);
    return () => {
      unsub();
      jQuery(window).off('scroll', onScroll);
    };
  });
</script>

<style style="text/scss" lang="scss">
  .layout-footer {
    text-align: center;
    padding-bottom: 30px;
    margin-top: -30px;
  }

  .content-wrapper {
    min-height: calc(100vh - 100px);
  }

  @media only screen and (min-width: 768px) and (max-width: 1024px) {
    .profile-page-wrapper.unloaded {
      display: none;
    }
  }
</style>

<svelte:head>
  {#if user && user.name}
    <title>{user.seo_title || user.name}</title>
    {#if user.no_index}
      <meta name="robots" content="noindex" />
    {/if}
    <meta name="description" content={user.seo_description} />
    <link rel="canonical" href={Url.seoFriendlyUrl(user.profile_url)} />
  {/if}
  {#if user && user.web_json_ld}
    {@html '<script type="application/ld+json">' + JSON.stringify(user.web_json_ld) + '</script>'}
  {/if}
</svelte:head>
<div class="profile-page-wrapper {browser ? '' : 'unloaded'}">
  <Viewport DESKTOP_MIN={1171} TABLET_MIN={795} let:mobile let:desktop let:tablet>
    <VisitorBanner mobile={mobile} tablet={tablet} desktop={desktop} fetchInviteCodeAutomaticallyUser={user} />

    <LinktreeBanner />
    <Header currentUser={currentUser} mobile={mobile} currentPage={pageName} />
    <div class="content-wrapper">
      {#if mobile}
        <ProfileMobile user={user} desktop={desktop} tablet={tablet} />
      {:else}
        <ProfileDesktop id={id} user={user} desktop={desktop} tablet={tablet} />
      {/if}
    </div>

    <div class="layout-footer">
      <TermsAndPrivacyFooter mobile={mobile} />
    </div>
  </Viewport>
</div>
